import { useNavigate, useParams } from "#routers/hooks";
import {
  AssetCreateMode,
  AssetCreateModeType,
} from "#src/batteries-included-components";
import {
  TemplatedConfigurationQueueType,
  templatedConfigurationRunQueueName,
} from "#src/batteries-included-components/Banners/TemplatedConfigurationRunStatus";
import { useListCustomAttributes } from "#src/components/hooks/adapters/useCustomAttributes";
import { useCreateOneTemplatedConfigurationRun } from "#src/components/hooks/adapters/useTemplatedConfigurations";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import {
  MultiStepFormProvider,
  useMultiStepFormContext,
} from "#src/hooks/useMultiStepForm";
import { useQueueUnique } from "#src/hooks/useQueue";
import { useStorageKey } from "#src/hooks/useStorageKey";
import {
  FacilitiesCreateRoute,
  FacilitiesListRoute,
} from "#src/routes/organization/facilities";
import { linkToFacilityDetail } from "#src/routes/organization/facilities/[facilityId]";
import { sanitizeCustomAttributeFormInputs } from "#src/utils/customAttributes";
import { useBreadcrumbsFromRoute } from "#src/utils/route";
import { useMutation } from "@tanstack/react-query";
import { Link, Page, Panel, useToast } from "@validereinc/common-components";
import {
  AssetType,
  FacilityAdapter,
  FacilitySchema,
  Resources,
} from "@validereinc/domain";
import {
  parseAsLatitudeDegree,
  parseAsLongitudeDegree,
} from "@validereinc/utilities";
import classNames from "classnames/bind";
import { pick } from "es-toolkit/compat";
import React from "react";
import { FacilityDetailsFormStep } from "./FacilityDetailsFormStep";
import styles from "./FacilityForm.module.scss";
import { FacilityOverviewFormStep } from "./FacilityOverviewFormStep";

const cx = classNames.bind(styles);

const CreateFacilityPageContent = ({
  assetCreateMode,
}: {
  assetCreateMode: AssetCreateModeType;
}) => {
  const [areTemplatedConfigurationsAvailable] = useIsFeatureAvailable({
    featureFlagQuery: "core:templated_configurations",
  });
  const [breadcrumbs] = useBreadcrumbsFromRoute(FacilitiesCreateRoute);
  const { stepper, footer } = useMultiStepFormContext();
  const { localize, isLoading: isMappingLoading } = useLocalization();
  const [filterConfigStorageKey] = useStorageKey(
    ["filter-configuration"],
    ["asset-builder", localize(AssetType.FACILITY)]
  );

  const shouldUseAssetBuilder =
    areTemplatedConfigurationsAvailable &&
    assetCreateMode === AssetCreateMode.ASSET_BUILDER;

  return (
    <Page
      title={`Create ${localize(AssetType.FACILITY)}`}
      category={
        shouldUseAssetBuilder
          ? "Asset Builder"
          : `Quick Create ${localize(AssetType.FACILITY)}`
      }
      shouldCenterContent
      breadcrumbs={breadcrumbs}
      footer={footer}
      isLoading={isMappingLoading}
    >
      <Panel
        className={cx("container")}
        isFluidY={false}
      >
        <div className={cx("stepper-container")}>{stepper}</div>
        <div className={cx("form-container")}>
          <FacilityOverviewFormStep
            filterConfigStorageKey={filterConfigStorageKey}
            // @ts-expect-error assetCreateMode is not yet implemented
            assetCreateMode={assetCreateMode}
            shouldUseAssetBuilder={shouldUseAssetBuilder}
          />
          <FacilityDetailsFormStep />
        </div>
      </Panel>
    </Page>
  );
};

const CreateFacilities = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { mode } = useParams<{ mode: AssetCreateModeType }>();
  const { localize } = useLocalization();

  const [queue, setQueue] = useQueueUnique<TemplatedConfigurationQueueType>(
    [],
    templatedConfigurationRunQueueName(Resources.FACILITY),
    (tx) => tx.job.id
  );

  const redirectAfterSuccess = () => {
    navigate({
      pathname: FacilitiesListRoute.path,
    });
  };

  const createTemplatedConfigurationRun = useCreateOneTemplatedConfigurationRun(
    {
      onSuccess: (data) => {
        const response = data.data;

        setQueue(
          queue.insert({
            job: {
              id: response.id,
              templateName: response.templated_configuration_name,
            },
            resourceDetails: {},
          })
        );
        redirectAfterSuccess();
      },
    }
  );

  const customAttributes =
    useListCustomAttributes({ filters: { entity_type: AssetType.FACILITY } })
      .data?.data ?? [];

  const createFacilityMutation = useMutation({
    mutationFn: (params) => {
      // @ts-expect-error TODO: fix adapter typing
      return FacilityAdapter.createOne(params);
    },
    onSuccess: (data) => {
      toast.push({
        intent: "success",
        description: (
          <>
            Successfully created {localize(AssetType.FACILITY)}.
            {mode === AssetCreateMode.QUICK_CREATE ? (
              <>
                {" "}
                <Link
                  label="View Details"
                  underline="always"
                  onClick={() =>
                    navigate({
                      pathname: linkToFacilityDetail(data.data.id),
                    })
                  }
                />
              </>
            ) : null}
          </>
        ),
      });
      redirectAfterSuccess();
    },
  });

  const handleOnSubmitClick = (values: Array<Record<string, any>>) => {
    const allValues = values.reduce(
      (total, current) => ({ ...total, ...current }),
      {}
    );

    if (allValues.template_name) {
      createTemplatedConfigurationRun.mutate({
        data: {
          ...pick(allValues, "inputs", "custom_attribute_inputs"),
        },
        meta: {
          templated_configuration_name: allValues.template_name,
        },
      });
    } else {
      // @ts-expect-error TODO: fix adapter typing
      createFacilityMutation.mutate({
        data: {
          ...pick(
            allValues,
            FacilitySchema.keyof().Enum.name,
            FacilitySchema.keyof().Enum.status
          ),
          latitude: parseAsLatitudeDegree(String(allValues?.coordinates?.[0])),
          longitude: parseAsLongitudeDegree(
            String(allValues?.coordinates?.[1])
          ),
          custom_attributes: sanitizeCustomAttributeFormInputs(
            allValues?.custom_attributes ?? {},
            customAttributes
          ),
        },
      });
    }
  };

  function handleOnCancelClick() {
    navigate({
      pathname: FacilitiesListRoute.path,
    });
  }

  return (
    <MultiStepFormProvider
      steps={[
        {
          label: "Overview",
        },
        {
          label: "Details",
        },
      ]}
      orientation="vertical"
      onCancel={handleOnCancelClick}
      onSubmit={handleOnSubmitClick}
      submissionActionText="Create"
      stepperClassName={cx("sticky-stepper")}
    >
      <CreateFacilityPageContent assetCreateMode={mode} />
    </MultiStepFormProvider>
  );
};

export default CreateFacilities;
